import { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from "react-router";

import {
    Box,
    Collapse,
    Divider,
    Heading,
    Spinner,
    useBreakpointValue
} from '@chakra-ui/react';

import { Header } from './Header.jsx';
import { AlertWrapper } from './Alerts.jsx';
import { LoginForm } from './Pages/LoginForm.jsx';
import { EventInfo } from './Pages/EventInfo.jsx';
import { VenueInfo } from './Pages/VenueInfo.jsx';
import { TimeslotForm } from './Pages/TimeslotForm.jsx';
import { RegistrationForm } from './Pages/RegistrationForm.jsx';
import { ShirtForm } from './Pages/ShirtForm.jsx';
import { SetCalendar } from './Pages/SetCalendar.jsx';
import { SongData } from './Pages/SongData.jsx';
import { Ranking } from './Pages/Ranking.jsx';

import { MISC } from '../helpers/misc.js';
import { Navigation } from './Navigation.jsx';

import moment from 'moment';
import 'moment-timezone';

const PageDisplay = (short, long, component) => {
    return (
        <Collapse in={short} animateOpacity unmountOnExit>
            <Heading size="lg">{long}</Heading>
            <Divider mt={4} mb={4} orientation='horizontal'/>
            {component}
        </Collapse>   
    );
}

const CurrentPageDisplay = ({pageValues}) => {
    const {
        eventConfig,
        currentPage,
        requestActive,
        sessionActive,
        changePage,
        eventOver,
        loginRequest,
        setups,
        fullSchedule,
        availSchedule,
        addressAllowed,
        schedAllowed,
        scheduleOnClick,
        schedCount,
        maxSlots,
        slotsRemaining,
        regAllowed,
        regTotal,
        regRemaining,
        shirtsAllowed,
        registerOnClick,
        calSchedule,
        buyBadge,
        recordBadge,
        buyShirts,
        recordShirts,
        getCharts,
        getRanking,
        rankingAllowed,
        songdataAllowed
    } = pageValues;

    const defaultPage = PageDisplay(
        currentPage,
        "Event Info",
        <EventInfo 
            eventConfig={eventConfig}
            eventOver={eventOver}
            sessionActive={sessionActive}
            setups={setups}
            changePage={changePage}
            addressAllowed={addressAllowed}
            schedAllowed={schedAllowed}
            regAllowed={regAllowed}
            regTotal={regTotal}
            songdataAllowed={songdataAllowed}
        /> 
    );  

    switch (currentPage) {
        case "log-in":
            if (!eventOver) {
                return PageDisplay(
                    currentPage,
                    "Log In",
                    <LoginForm
                        eventConfig={eventConfig}
                        requestActive={requestActive}
                        loginRequest={loginRequest}
                    />
                );
            } else {
                return defaultPage;
            }
        case "event-info":
            return PageDisplay(
                currentPage,
                "Event Info",
                <EventInfo
                    eventConfig={eventConfig}
                    eventOver={eventOver}
                    sessionActive={sessionActive}
                    setups={setups}
                    slotsRemaining={slotsRemaining}
                    changePage={changePage}
                    addressAllowed={addressAllowed}
                    schedAllowed={schedAllowed}
                    regAllowed={regAllowed}
                    regTotal={regTotal}
                    songdataAllowed={songdataAllowed}
                /> 
            );  
        case "venue-info":
            return PageDisplay(
                currentPage,
                "Venue Info",
                <VenueInfo
                    eventConfig={eventConfig}
                    sessionActive={sessionActive}
                    changePage={changePage}
                    addressAllowed={addressAllowed}
                    schedAllowed={schedAllowed}
                    regAllowed={regAllowed}
                /> 
            );
        case "sched-set":
            if (schedAllowed) {
                return PageDisplay(
                    currentPage,
                    "Schedule Set",
                    <TimeslotForm
                        eventConfig={eventConfig}
                        requestActive={requestActive}
                        setups={setups}
                        fullSchedule={fullSchedule}
                        availSchedule={availSchedule}
                        schedAllowed={schedAllowed}
                        schedCount={schedCount}
                        maxSlots={maxSlots}
                        slotsRemaining={slotsRemaining}
                        scheduleOnClick={scheduleOnClick}
                        buyBadge={buyBadge}
                        recordBadge={recordBadge}
                    /> 
                );  
            } else {
                return defaultPage;
            }
        case "register":
            if (regAllowed) {
                return PageDisplay(
                    currentPage,
                    "Register For Event",
                    <RegistrationForm
                        eventConfig={eventConfig}
                        requestActive={requestActive}
                        regAllowed={regAllowed}
                        regRemaining={regRemaining}
                        buyBadge={buyBadge}
                        recordBadge={recordBadge}
                        registerOnClick={registerOnClick}
                    /> 
                );      
            } else {
                return defaultPage;
            }
        case "buy-shirts":
            if (shirtsAllowed) {
                return PageDisplay(
                    currentPage,
                    "Purchase Shirts",
                    <ShirtForm
                        eventConfig={eventConfig}
                        buyShirts={buyShirts}
                        recordShirts={recordShirts}
                        requestActive={requestActive}
                    /> 
                );
            } else {
                return defaultPage;
            }
        case "ranking":
            if (rankingAllowed) {
                return PageDisplay(
                    currentPage,
                    "Ranking",
                    <Ranking
                        eventConfig={eventConfig}
                        getRanking={getRanking}
                    /> 
                );
            } else {
                return defaultPage;
            }            
        case "calendar":
            return PageDisplay(
                currentPage,
                "Set Calendar",
                <SetCalendar
                    eventConfig={eventConfig}
                    calSchedule={calSchedule}
                    setups={setups}
                /> 
            );
        case "song-data":
            if (songdataAllowed) {
                return PageDisplay(
                    currentPage,
                    "Song Data",
                    <SongData
                        eventConfig={eventConfig}
                        getCharts={getCharts}
                    /> 
                );
            } else {
                return defaultPage;
            }
        case "check-in":
            return PageDisplay(
                currentPage,
                "Check In",
                <LoginForm
                    eventConfig={eventConfig}
                    requestActive={requestActive}
                    loginRequest={loginRequest}
                    isCheckIn={1}
                />
            );
        default:
            return PageDisplay(
                currentPage,
                "Loading...",
                <Spinner marginLeft="auto" marginRight="auto" size='xl' />
            );                                                          
    }
}

export function Content({configVersion, initialEventConfig}) {
    let location = useLocation();
    const navigate = useNavigate();

    const [eventConfig, setEventConfig] = useState(initialEventConfig);
    const [requestActive, setRequestActive] = useState(false);
    const [setups, setSetups] = useState({})
    const [calSchedule, setCalSchedule] = useState({});
    const [fullSchedule, setFullSchedule] = useState({});
    const [availSchedule, setAvailSchedule] = useState({});
    const [eventOver, setEventover] = useState(initialEventConfig["eventOver"]);
    const [globalRegAllowed, setGlobalRegAllowed] = useState(false);
    const [globalSchedAllowed, setGlobalSchedAllowed] = useState(false);
    const [regTotal, setRegTotal] = useState(0);
    const [regRemaining, setRegRemaining] = useState(0);
    const [sessionActive, setSessionActive] = useState(false);
    const [sessionAttempted, setSessionAttempted] = useState(false);
    const [memberId, setMemberId] = useState(false)
    const [regCount, setRegCount] = useState(0);
    const [specCount, setSpecCount] = useState(0);
    const [schedCount, setSchedCount] = useState(0);
    const [shirtCount, setShirtCount] = useState(0);
    const [tplp, setTplp] = useState(0);
    const [currentPage, setCurrentPage] = useState("event-info");
	const [currentAlertId, setCurrentAlertId] = useState(0);
	const [alertList, setAlertList] = useState({});

	const commonMethods = {
		// alerts
		createDialog: (payload) => {
			const newAlertList = alertList;
			
			newAlertList[currentAlertId] = payload;
			setAlertList({...newAlertList});
			setCurrentAlertId(currentAlertId + 1);
		},
		closeDialog: (alertId) => {
			const newAlertList = alertList;
			
			delete newAlertList[alertId];
			setAlertList({...newAlertList});
		},
		confirmDialog: (alertId) => {
			const newAlertList = alertList;
			
			delete newAlertList[alertId];
			setAlertList({...newAlertList});
		},
        reloadEventConfig: async () => {
            const newEventConfig = await MISC.fetchJson(`config/config.json?v=${configVersion}`);

            newEventConfig["startDate"] = `${newEventConfig["eventDates"][0]["date"]} ${newEventConfig["eventDates"][0]["start"]}`;

            setEventConfig({...newEventConfig});

            document.title = newEventConfig["liveLong"]

            return newEventConfig;
        }
			
	};

    const commonMethodsRef = useRef(commonMethods);
    commonMethodsRef.current = commonMethods;

    const contentTopPadding = useBreakpointValue(
        {
          "base": ()=> 6,
          "xl": ()=>  16
        }
    );        

    const maxSlots = tplp >= eventConfig["doubleSlotCutoff"] ? 2 : 1;
    const slotsRemaining = maxSlots - schedCount;
    const addressAllowed = !eventOver && sessionActive && regCount > 0;
    const regAllowed = !eventOver && sessionActive && globalRegAllowed && regCount === 0;
    const schedAllowed = !eventOver && sessionActive && globalSchedAllowed && regCount > 0 && specCount === 0 && schedCount < maxSlots;
    const shirtsAllowed = !eventOver && sessionActive && regCount > 0 && !eventConfig["shirtsOver"] && shirtCount < 1;
    const clearAllowed = !eventOver && sessionActive && regCount > 0 && specCount === 0 && schedCount > 0;
    const rankingAllowed = eventConfig["showRanking"];
    const songdataAllowed = eventConfig["showSongdata"];

    const clearSession = async () => {
        setRegCount(0);
        setSpecCount(0);
        setSchedCount(0);
        setTplp(0);
        setSessionActive(false);
    }

    const setEventDefaults = (response) => {
        try {
            commonMethods.reloadEventConfig();

            if (!response?.data || response.data?.length < 1) {
                throw new Error("Invalid API response (no data)");
            }

            if (!response?.data?.setups || Object.keys(response.data.setups)?.length < 1) {
                throw new Error("Invalid API response (no setups)");
            }

            const newSetups = response.data.setups;
            setSetups({...newSetups});

            if (!response?.data?.schedule || Object.keys(response.data.schedule)?.length < 1) {
                throw new Error("Invalid API response (no schedule)");
            }

            const newSchedule = response.data.schedule;

            if (!newSchedule?.calendar || Object.keys(newSchedule.calendar)?.length < 1) {
                throw new Error("Invalid API response (no calendar schedule)");
            }

            setCalSchedule({...newSchedule.calendar});

            if (!newSchedule?.full || Object.keys(newSchedule.full)?.length < 1) {
                throw new Error("Invalid API response (no full schedule)");
            }

            setFullSchedule({...newSchedule.full});

            if (newSchedule?.availableTimes && Object.keys(newSchedule.availableTimes)?.length > 0) {
                setAvailSchedule({...newSchedule.availableTimes});
            }

            if (!response?.data?.capacity || Object.keys(response.data.capacity)?.length < 1) {
                throw new Error("Invalid API response (no capacity)");
            }

            const newCapacity = response.data.capacity;

            if (!newCapacity.hasOwnProperty("regAllowed")) {
                throw new Error("Invalid API response (no regAllowed)");
            }

            setGlobalRegAllowed(newCapacity.regAllowed);

            if (!newCapacity.hasOwnProperty("regTotal") || !MISC.isInt(newCapacity.regTotal)) {
                throw new Error("Invalid API response (no regTotal)");
            }

            setRegTotal(newCapacity.regTotal);              

            if (!newCapacity.hasOwnProperty("regRemaining") || !MISC.isInt(newCapacity.regRemaining)) {
                throw new Error("Invalid API response (no regRemaining)");
            }

            setRegRemaining(newCapacity.regRemaining);

            if (!newCapacity.hasOwnProperty("schedAllowed")) {
                throw new Error("Invalid API response (no schedAllowed)");
            }

            setGlobalSchedAllowed(newCapacity.schedAllowed);
        } catch (e) {
            throw new Error(e);
        }
    }

    const setPlayerDefaults = (response) => {
        try {
            if (!response?.data.hasOwnProperty("memberId") || !MISC.isInt(response.data.memberId)) {
                throw new Error("Invalid API response (no memberId)");
            }

            setMemberId(response.data.memberId);

            if (!response?.data?.playerRegCounts || Object.keys(response.data.playerRegCounts)?.length < 1) {
                throw new Error("Invalid API response (no player counts)");
            }

            const newPlayerCounts = response.data.playerRegCounts;

            if (!newPlayerCounts.hasOwnProperty("PLAYER_REG_COUNT") || !MISC.isInt(newPlayerCounts.PLAYER_REG_COUNT)) {
                throw new Error("Invalid API response (no PLAYER_REG_COUNT)");
            }

            setRegCount(newPlayerCounts.PLAYER_REG_COUNT);

            if (!newPlayerCounts.hasOwnProperty("PLAYER_SPEC_COUNT") || !MISC.isInt(newPlayerCounts.PLAYER_SPEC_COUNT)) {
                throw new Error("Invalid API response (no PLAYER_SPEC_COUNT)");
            }

            setSpecCount(newPlayerCounts.PLAYER_SPEC_COUNT);

            if (!newPlayerCounts.hasOwnProperty("PLAYER_SCHED_COUNT") || !MISC.isInt(newPlayerCounts.PLAYER_SCHED_COUNT)) {
                throw new Error("Invalid API response (no PLAYER_SCHED_COUNT)");
            }

            setSchedCount(newPlayerCounts.PLAYER_SCHED_COUNT);

            if (!newPlayerCounts.hasOwnProperty("PLAYER_SHIRT_COUNT") || !MISC.isInt(newPlayerCounts.PLAYER_SHIRT_COUNT)) {
                throw new Error("Invalid API response (no PLAYER_SHIRT_COUNT)");
            }

            setShirtCount(newPlayerCounts.PLAYER_SHIRT_COUNT);
            
            if (!response.data.hasOwnProperty("playerTPLP") || !MISC.isInt(response.data.playerTPLP)) {
                throw new Error("Invalid API response (no player TP/LP)");
            }

            setTplp(response.data.playerTPLP);
        } catch (e) {
            throw new Error(e);
        }
    }

    const retrieveSession = async (isFirstRetrieval) => {
        try {
            setRequestActive(true);

            const requestType = "retrieve-session";

            const requestPayload = {};
    
            const response = await MISC.apiRequest(requestType, requestPayload);

            setEventDefaults(response);

            setSessionAttempted(true);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setPlayerDefaults(response);

            setRequestActive(false);
            setSessionActive(true);
        } catch (e) {
            setRequestActive(false);
            clearSession();

            if (e?.eventOver) {
                setEventover(true);
            }

            MISC.apiSendError("", e?.message, !isFirstRetrieval ? commonMethods : null);
        }
    }

    const loginRequest = async (username, password, isCheckIn=0) => {
        try {
            setRequestActive(true);

            if (username?.length < 1) {
                throw new Error("Please enter a user name.");
            }

            if (password?.length < 1) {
                throw new Error("Please enter a password.");
            }

            const requestType = "gs-login";

            const requestPayload = {
                name: username,
                pass: password,
                isCheckIn: isCheckIn
            };
    
            const response = await MISC.apiRequest(requestType, requestPayload);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            if (isCheckIn === 1) {
                commonMethodsRef.current.createDialog({
                    header:"Success", 
                    body:"Successfully checked in for the event!"
                });
            }

            setPlayerDefaults(response);

            changePage("event-info");

            setRequestActive(false);
            setSessionActive(true);
        } catch (e) {
            setRequestActive(false);
            clearSession();

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Login failed`, e?.message, commonMethods, "Login Failure");
        }
    };

    const scheduleSet = async (scheduleId, setupId, byop) => {

        try {
            const newEventConfig = await commonMethods.reloadEventConfig();

            if (newEventConfig["eventOver"]) {
                throw new Error("Event is already over");
            }

            if (regCount < 1) {
                throw new Error("You must register as a competitor before you can schedule a set");
            }
            
            if (specCount > 0) { 
                throw new Error("Already registered as spectator");
            }

            if (schedCount >= maxSlots) {
                throw new Error("You've already registered the maximum number of slots");
            }

            if (!MISC.isInt(scheduleId)) {
                throw new Error("Invalid schedule slot");
            }

            if (!MISC.isInt(setupId)) {
                throw new Error("Invalid setup");
            }

            if (!MISC.isBoolean(byop)) {
                throw new Error("Invalid byop");
            }

            const scheduleDateTime = moment.tz(fullSchedule[scheduleId]["schedule_time_start"], "America/New_York");

            const weekday = scheduleDateTime.format("dddd, MMMM Do YYYY");

            const time = scheduleDateTime.format("h:mm a");

            const setupName = setups[setupId]["setup_name"];

            let overridePromise = new Promise( (resolve, reject) => {
                commonMethodsRef.current.createDialog({
                    header:"Confirm Submission", 
                    body:`You're scheduling your set for:
                        <br/><br/>
                        <strong>${weekday} at ${time}</strong>
                        <br/><br/>
                        ...on this setup:
                        <br/><br/>
                        <strong>${setupName}</strong>           
                        <br/><br/>
                        Is this correct?
                    `,
                    confirmLabel: "Schedule",
                    
                    confirmFunc: () => {
                        resolve(true);
                    },
                    declineFunc: () => {
                        resolve(false);
                    }
                });
            });
    
            const overrideResponse = await overridePromise;
            if (!overrideResponse) {
                return false;
            }

            setRequestActive(true);

            const requestType = "schedule-set";

            const requestPayload = {
                scheduleId: scheduleId,
                byop: byop
            };
    
            const response = await MISC.apiRequest(requestType, requestPayload);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            if (!response?.data || response.data?.length < 1) {
                throw new Error("Invalid API response");
            }

            setPlayerDefaults(response);

            commonMethodsRef.current.createDialog({
                header:"Success", 
                body:"Set scheduled successfully!"
            });

            changePage("venue-info");

            setRequestActive(false);
            
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Schedule submission failed`, e?.message, commonMethods, "Submission Failure");
        }

    };

    const clearSetRequest = async () => {

        try {
            let overridePromise = new Promise( (resolve, reject) => {
                commonMethodsRef.current.createDialog({
                    header:"Confirm Clear", 
                    body:"Are you sure you want to clear your sets?",
                    confirmLabel: "Clear Sets",
                    
                    confirmFunc: () => {
                        resolve(true);
                    },
                    declineFunc: () => {
                        resolve(false);
                    }
                });
            });
    
            const overrideResponse = await overridePromise;
            if (!overrideResponse) {
                return false;
            }

            setRequestActive(true);

            const requestType = "clear-sets";            
    
            const response = await MISC.apiRequest(requestType);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setPlayerDefaults(response);

            commonMethodsRef.current.createDialog({
                header:"Success", 
                body:"Sets cleared successfully."
            });
            
            changePage("event-info");

            setRequestActive(false);
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }

            changePage("event-info");
            
            MISC.apiSendError(`Clear sets failed`, e?.message, commonMethods, "Clear Sets Failure");
        }

    };

    const registerSpectator = async () => {

        try {
            setRequestActive(true);

            const requestType = "register-spectator";
    
            const response = await MISC.apiRequest(requestType);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setPlayerDefaults(response);

            commonMethodsRef.current.createDialog({
                header:"Success", 
                body:"Successfully registered as a spectator!"
            });

            changePage("venue-info");

            setRequestActive(false);
            
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Registration failed`, e?.message, commonMethods, "Registration Failure");
        }

    };

    const buyBadge = async (data, actions, spectatorFlag) => {

        try {
            setRequestActive(true);

            const newEventConfig = await commonMethods.reloadEventConfig();

            if (newEventConfig["eventOver"]) {
                throw new Error("Event is already over");
            }

            if (regCount > 0) {
                throw new Error("Already registered");
            }

            let currentTime = new Date();

            currentTime = currentTime.getTime();

            //we want transactions within an hour to share an invoice id to try to avoid double clicks (paypal will block identical invoice ids)
            currentTime = Math.floor(currentTime / 1000 / 60 / 60);

            const newInvoiceId = `${newEventConfig["liveShort"]}-badge-${memberId}-${currentTime}`

            const requestPayload = {
                spectatorFlag: spectatorFlag,
                badgeTransactionId: newInvoiceId
            };

            const response = await MISC.apiRequest("badge-precheck", requestPayload);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setPlayerDefaults(response);

            const badgeCost = spectatorFlag === 0
                ? newEventConfig["competitorPrice"]
                : newEventConfig["spectatorPrice"]

            const orderResult = await actions.order.create({
                purchase_units: [{
                    amount: {
                        value: badgeCost
                    },
                    invoice_id: newInvoiceId
                }]
            });

            setRequestActive(false);

            return orderResult;
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Badge purchase failed (order)`, e?.message, commonMethods, "Badge Purchase Failure");

            return false;
        }

    }

    const recordBadge = async (data, actions, spectatorFlag) => {

        try {

            setRequestActive(true);

            const approveResult = await actions.order.capture();

            // this is not actually a paypal transaction id because the api sucks, it's gonna be the invoice id and I ain't changing the name
            const transactionId = approveResult?.purchase_units?.[0]?.invoice_id
                ? approveResult.purchase_units[0].invoice_id
                : data?.orderID ?? "";

            const requestPayload = {
                spectatorFlag: spectatorFlag,
                badgeTransactionId: transactionId
            };

            const response = await MISC.apiRequest("register-attendee", requestPayload);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setPlayerDefaults(response);

            const typeVerbiage = spectatorFlag === 0
                ? "competitor"
                : "spectator";

            const shirtsOver = eventConfig["shirtsOver"];

            commonMethodsRef.current.createDialog({
                header: "Success", 
                body: shirtsOver
                    ? `Purchase successful!  You are now registered as a ${typeVerbiage}!`
                    : `Purchase successful!  You are now registered as a ${typeVerbiage}!  Please consider buying a shirt to support the venue!`
                
            });                

            if (!eventConfig["shirtsOver"]) {
                changePage("buy-shirts");
            } else {
                changePage("event-info");
            }   

            setRequestActive(false);

            return approveResult;
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Badge purchase failed (approval)`, e?.message, commonMethods, "Badge Purchase Failure");

            return false;
        }
    }

    const buyShirts = async (data, actions, totalCost) => {

        try {
            setRequestActive(true);

            const newEventConfig = await commonMethods.reloadEventConfig();

            if (newEventConfig["eventOver"]) {
                throw new Error("Event is already over");
            }

            if (newEventConfig["shirtsOver"]) {
                throw new Error("Shirt sales have closed");
            }

            let currentTime = new Date();

            currentTime = currentTime.getTime();

            //we want transactions within an hour to share an invoice id to try to avoid double clicks (paypal will block identical invoice ids)
            currentTime = Math.floor(currentTime / 1000 / 60 / 60);

            const newInvoiceId = `${newEventConfig["liveShort"]}-shirt-${memberId}-${currentTime}`

            const orderResult = await actions.order.create({
                purchase_units: [{
                    amount: {
                        value: totalCost
                    },
                    invoice_id: newInvoiceId
                }]
            });

            setRequestActive(false);

            return orderResult;
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Shirt purchase failed (order)`, e?.message, commonMethods, "Shirt Purchase Failure");

            return false;
        }

    }

    const recordShirts = async (data, actions, shirtQuants) => {

        try {

            setRequestActive(true);

            const approveResult = await actions.order.capture();

            // this is not actually a paypal transaction id because the api sucks, it's gonna be the invoice id and I ain't changing the name
            const transactionId = approveResult?.purchase_units?.[0]?.invoice_id
                ? approveResult.purchase_units[0].invoice_id
                : data?.orderID ?? "";

            const requestType = "record-shirts";

            const requestPayload = {
                shirtQuants: shirtQuants,
                shirtTransactionId: transactionId
            };

            const response = await MISC.apiRequest(requestType, requestPayload);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setPlayerDefaults(response);

            commonMethodsRef.current.createDialog({
                header:"Success", 
                body:"Shirt purchase successful!"
            });

            changePage("event-info");

            setRequestActive(false);

            return approveResult;
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Shirt purchase failed (approval)`, e?.message, commonMethods, "Shirt Purchase Failure");

            return false;
        }
    }

    const getCharts = async () => {

        try {
            setRequestActive(true);

            const requestType = "get-charts";            
    
            const response = await MISC.apiRequest(requestType);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            if (!response?.data?.chartData || Object.keys(response.data.chartData)?.length < 1) {
                throw new Error("Invalid API response (no chartData)");
            }

            const newChartData = response.data.chartData;
            
            setRequestActive(false);

            return(newChartData);
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Get Ranking failed`, e?.message, commonMethods, "Get Ranking Failure");

            return([]);
        }

    };

    const getRanking = async () => {

        try {
            setRequestActive(true);

            const requestType = "get-ranking";            
    
            const response = await MISC.apiRequest(requestType);

            setEventDefaults(response);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            if (!response?.data?.chartData || Object.keys(response.data.chartData)?.length < 1) {
                throw new Error("Invalid API response (no chartData)");
            }

            if (!response?.data?.rankingData) {
                throw new Error("Invalid API response (no rankingData)");
            }            

            const newGetRankingData = response.data;
            
            setRequestActive(false);

            return(newGetRankingData);
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }
            
            MISC.apiSendError(`Get Ranking failed`, e?.message, commonMethods, "Get Ranking Failure");

            return([]);
        }

    };    

    const logoutRequest = async () => {

        try {
            setRequestActive(true);

            const requestType = "gs-logout";
    
            const response = await MISC.apiRequest(requestType);

            if (MISC.apiHasError(response)) {
                const errorStr = MISC.apiGetErrorStr(response);
                throw errorStr;
            }

            setRequestActive(false);
            changePage("log-in");
            clearSession();
        } catch (e) {
            setRequestActive(false);

            if (e?.eventOver) {
                setEventover(true);
            }

            changePage("log-in");
            
            MISC.apiSendError(`Logout failed`, e?.message, commonMethods, "Logout Failure");
        }

    };

    const changePage = (newPage) => {
        navigate(`/${newPage}`, { replace: true });
        setCurrentPage(newPage);
        window.scrollTo(0, 0);
    };

    const pageValues = {
        eventConfig: eventConfig,
        currentPage: currentPage,
        sessionActive: sessionActive,
        requestActive: requestActive,
        changePage: changePage,
        eventOver: eventOver,
        loginRequest: loginRequest,
        setups: setups,
        fullSchedule: fullSchedule,
        availSchedule: availSchedule,
        addressAllowed: addressAllowed,
        schedAllowed: schedAllowed,
        schedCount: schedCount,
        maxSlots: maxSlots,
        slotsRemaining: slotsRemaining,
        scheduleOnClick: scheduleSet,
        regTotal: regTotal,
        regAllowed: regAllowed,
        regRemaining: regRemaining,
        shirtsAllowed: shirtsAllowed,
        registerOnClick: registerSpectator,
        calSchedule: calSchedule,
        buyBadge: buyBadge,
        recordBadge: recordBadge,
        buyShirts: buyShirts,
        recordShirts: recordShirts,
        getCharts: getCharts,
        getRanking: getRanking,
        rankingAllowed: rankingAllowed,
        songdataAllowed: songdataAllowed
    }

    useEffect(()=> {
        if (sessionAttempted) {
            let newPage = location?.pathname?.split("/");
            newPage = newPage?.[1]
                ? newPage[1]
                : "event-info";

            if (newPage !== currentPage) {
                changePage(newPage);
            }
        }
    }, [location, sessionAttempted]);

    useEffect(()=> {
        retrieveSession(true);
    }, []);

    try {
        return (
            <>
                <Header liveAbbrev={eventConfig["liveAbbrev"]} changePage={changePage}/>
                <Box maxWidth="1400px" ml="auto" mr="auto">
                    <Navigation requestActive={requestActive} sessionActive={sessionActive} currentPage={currentPage} addressAllowed={addressAllowed} schedAllowed={schedAllowed} regAllowed={regAllowed} regTotal={regTotal} shirtsAllowed={shirtsAllowed} shirtsOver={eventConfig["shirtsOver"]} clearAllowed={clearAllowed} rankingAllowed={rankingAllowed} songdataAllowed={songdataAllowed} eventOver={eventOver} pageOnClick={(page)=>{changePage(page)}} clearSetRequest={clearSetRequest} logoutRequest={logoutRequest}/>
                    <Box bgColor="gray.800" padding={contentTopPadding}>
                        <CurrentPageDisplay pageValues={pageValues}/>
                    </Box>
                </Box>
                <AlertWrapper commonMethods={commonMethods} alertList={alertList}/>
            </>
        );
    } catch (e) {
        console.error("Content render error: ", e);
    }    
}
